<script setup lang="ts">
import { SlidersHorizontal } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, defineCustomElement, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Product } from '@/types/products.ts';

import MobileProductFilters from '@/components/mode-list-filters/MobileProductFilters.vue';
import TheDistributorFilter from '@/components/mode-list-filters/TheDistributorFilter.vue';
import TheDurationFilter from '@/components/mode-list-filters/TheDurationFilter.vue';
import TheMultiselectFilter from '@/components/mode-list-filters/TheMultiselectFilter.vue';
import ThePriceFilter from '@/components/mode-list-filters/ThePriceFilter.vue';
import TheThemesFilter from '@/components/mode-list-filters/TheThemesFilter.vue';
import TheTimeRangeFilter from '@/components/mode-list-filters/TheTimeRangeFilter.vue';
import TheTypeFilter from '@/components/mode-list-filters/TheTypeFilter.vue';
import TheCalendar from '@/components/mode-list/TheCalendar.vue';
import TheCard from '@/components/mode-list/TheCard.vue';
import TheMobileSlider from '@/components/mode-list/TheMobileSlider.vue';
import TheSelectDropdown from '@/components/mode-list/TheSelectDropdown.vue';
import TheMapList from '@/components/mode-map/TheMapList.vue';
import TheMapSearch from '@/components/mode-map/TheMapSearch.vue';
import useModeMap from '@/composables/useModeMap';
import useResponsive from '@/composables/useResponsive';
import ThePopupCardCE from '@/lib/ThePopupCardCE.ce.vue';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { useProductsStore } from '@/stores/useProductsStore';
import { Language } from '@/types/locale';
import { formatPrice, hasActiveFilters } from '@/utils/utils';

const emit = defineEmits(['close']);

const { isMobile } = useResponsive();
const { t } = useI18n();

const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const metricsStore = useMetricsStore();
const { places, themes, types, taxonomyFilter } = storeToRefs(metricsStore);

const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration, currentLocale } = storeToRefs(globalConfigurationStore);

const productsStore = useProductsStore();
const { products } = storeToRefs(productsStore);

const {
  map,
  createCloseMapButton,
  createSliderToggleButton,
  mountMap,
  unMountMap,
  getBoundingBox,
  isLoadingMapMovement,
  lastValidBoundingBox,
} = useModeMap();

const filtersResetKey = ref(0);
const showProductSlider = ref<boolean>(true);
const isFiltersOpen = ref<boolean>(false);
const pins = ref<Product[]>();

function isValidGPS(gps?: { lon?: number; lat?: number }): boolean {
  return !!(gps?.lon && gps?.lat && gps.lon !== 0 && gps.lat !== 0);
}

const validPins = computed(() => {
  const allPins = pins.value ?? [];
  return allPins.filter((pin) => {
    const isValid = isValidGPS(pin.location?.gps);

    if (!isValid) {
      console.warn(`[Mode Map] Produit ignoré pour géolocalisation invalide :`, {
        id: pin.id,
        name: pin.name,
        lat: pin.location?.gps?.lat,
        lon: pin.location?.gps?.lon,
      });
    }

    return isValid;
  });
});
const currentProduct = ref<Product>();
const initialMapPosition = ref();
const hasCalendar = computed(() => globalConfiguration.value?.options?.hasCalendar ?? true);
const prefixUrl = computed(() => globalConfiguration.value?.prefixUrl ?? null);
const mapLanguage = computed(() => {
  return currentLocale.value === Language.FRENCH ? Language.FRENCH : Language.ENGLISH;
});
let listener: EventListener | null = null;
let popupCard: HTMLElement & {
  product: Product;
  showFavorite: boolean;
  prefixUrl: string | null;
} | null = null;
let currentFetchController: AbortController | null = null;

function handleHoverProduct(productId: any) {
  const pinIndex = pins.value?.findIndex((pin: any) => pin.id === productId);
  if (pinIndex !== -1 && pinIndex !== undefined && map.value) {
    map.value.setHoveredPinIndex(pinIndex);
  }
}

const sortingOptions2 = [
  { label: t('menu.relevance'), value: 'random' },
  { label: t('menu.price_asc'), value: 'price_from_ascending' },
  { label: t('menu.price_desc'), value: 'price_from_descending' },
  { label: t('menu.ranking'), value: 'rating' },
];

const timeRangeOptions = [
  { label: t('timeRange.morning'), minTime: 480, maxTime: 720, count: 0 },
  { label: t('timeRange.afternoon'), minTime: 720, maxTime: 1020, count: 0 },
  { label: t('timeRange.evening'), minTime: 1020, maxTime: 1440, count: 0 },
];

onMounted(async () => {
  isLoadingMapMovement.value = true;
  mountMap();

  const thePopupCard = defineCustomElement(ThePopupCardCE);
  if (customElements.get('yb-popup-card') === undefined) {
    customElements.define('yb-popup-card', thePopupCard);
  }

  // @ts-expect-error cannot find module
  const { TouristicMap } = await import('https://api.touristicmaps.com/lib/mod.mjs');

  map.value = new TouristicMap({
    interaction: 'on',
    pitch_rotate_interaction: 'rotate_only',
    layers_control_position: 'none',
    fullscreen_control_position: 'none',
    fit_interaction: 'geolocation',
    fit_control_position: 'bottom-right',
    zoom_interaction: 'on',
    zoom_control_position: isMobile.value ? 'none' : 'bottom-right',
    attribution_control_position: isMobile.value ? 'none' : 'bottom-left',
    language: mapLanguage.value,
  });

  const component = document.querySelector('yb-list');
  const mapWrapper = component?.shadowRoot?.getElementById('map_wrapper');
  mapWrapper?.appendChild(map.value);

  const { symbol: closeMapSymbol, element: closePanel } = createCloseMapButton(emit, isMobile.value, t('map.close'));
  const { symbol: closeSliderSymbol, element: closeSliderPanel } = createSliderToggleButton(showProductSlider);

  map.value.addControl(closeMapSymbol, 'top-right', closePanel);
  if (!isMobile.value) {
    map.value.addControl(closeSliderSymbol, 'top-left', closeSliderPanel);
  }

  await productsStore.fetchProductsList(filters.value).then(async () => {
    const bounds = map.value.markerBounds();
    if (bounds) {
      initialMapPosition.value = {
        center: bounds.center,
        range: bounds.range,
        pitch: 0,
        roll: 0,
        bearing: 0,
        animate: true,
      };
      map.value.position(initialMapPosition.value);

      if (products.value.length > 0) {
        lastValidBoundingBox.value = { center: bounds.center, range: bounds.range };
      }
    }
  });

  const handleMoveEnd = async (e: any) => {
    if (!map.value || currentProduct.value !== undefined) {
      return;
    }

    if (currentFetchController) {
      currentFetchController.abort();
      isLoadingMapMovement.value = false;
    }

    currentFetchController = new AbortController();

    const { detail: { center } } = e;
    const range = map.value.range();
    const bounds = getBoundingBox(center, range);
    filtersStore.addBoundingBoxFilter(bounds);
    isLoadingMapMovement.value = true;
    try {
      await productsStore.fetchProductsList(filters.value, currentFetchController.signal);
    }
    catch (e: any) {
      if (e.name === 'AbortError') {
        return;
      }
    }

    if (products.value.length > 0) {
      lastValidBoundingBox.value = { center, range };
    }
  };

  setTimeout(() => {
    map.value.addEventListener('moveend', handleMoveEnd);
  }, 1000);
});

watch(
  () => products.value,
  async () => {
    if (!map.value) {
      return;
    }
    if (!isFiltersOpen.value) {
      pins.value = products.value;
      await updateMapMarkers();
      isLoadingMapMovement.value = false;
    }
  },
);

watch(
  () => filters.value,
  () => {
    if (map.value && !isFiltersOpen.value) {
      isLoadingMapMovement.value = true;
    }
  },
  { deep: true },
);

onBeforeUnmount(() => {
  if (currentFetchController) {
    currentFetchController.abort();
    currentFetchController = null;
  }
  unMountMap();
});

window.onbeforeunload = function () {
  return unMountMap();
};

async function updateMapMarkers() {
  if (!map.value || currentProduct.value !== undefined) {
    return;
  }

  const model = {
    pins: validPins.value,
    coordinates(pin: Product) {
      return [pin.location?.gps?.lon, pin.location?.gps?.lat];
    },
    default: {
      em_size: 4,
      color: {
        default: 'var(--yb-color-primary)',
        hover: 'var(--yb-color-primary-hover)',
        selection: 'var(--yb-color-dark-primary)',
      },
    },
    render(pin: Product) {
      return {
        em_size: 3,
        svg_text: formatPrice(pin.priceFrom, undefined, !Number.isInteger(pin.priceFrom)),
        color: {
          default: 'var(--yb-color-primary)',
          hover: 'var(--yb-color-primary-hover)',
          selection: 'var(--yb-color-dark-primary)',
        },
        onclick(pin: Product) {
          if (currentFetchController) {
            currentFetchController.abort();
            currentFetchController = null;
            isLoadingMapMovement.value = false;
          }
          currentProduct.value = pin;
        },
        popup(pin: Product, _index: number, close: () => void) {
          if (isMobile.value) {
            return null;
          }
          if (popupCard?.product?.id !== pin.id) {
            popupCard = document.createElement('yb-popup-card') as HTMLElement & {
              product: Product;
              showFavorite: boolean;
              prefixUrl: string | null;
            };
            popupCard.product = pin;
            popupCard.showFavorite = true;
            popupCard.prefixUrl = prefixUrl.value;
          }
          popupCard.addEventListener('close-popup', () => {
            close();
            currentProduct.value = undefined;
          });
          map.value.removeEventListener('click', listener as EventListener);
          listener = (e: any) => {
            let clickedOnPopup = false;
            let target = e.target;
            while (true) {
              if (target === popupCard) {
                clickedOnPopup = true;
                break;
              }
              if (target === null) {
                break;
              }
              target = target.parentElement;
            }
            if (clickedOnPopup)
              return;
            map.value.removeEventListener('click', listener as EventListener);
            listener = null;
            close();
            currentProduct.value = undefined;
          };
          setTimeout(() => map.value.addEventListener('click', listener as EventListener), 0);
          return popupCard;
        },
      };
    },
  };

  map.value.setMarkers(model);
}

async function handleProductsFilter() {
  isFiltersOpen.value = false;
  isLoadingMapMovement.value = true;
  filtersStore.saveToStorage(filters.value);
  await productsStore.fetchProductsList(filters.value);
}

async function resetProductFilters(isForceReset: boolean = false) {
  if (isForceReset)
    isLoadingMapMovement.value = true;

  // detele PriceRange filter
  filters.value.filters = filters.value.filters.filter(it => it.filterProperties.type !== 'priceFrom');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties.type !== 'priceFrom');
  // delete Duration filter
  filters.value.filters = filters.value.filters.filter(it => it.filterProperties.type !== 'sessionDuration');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties.type !== 'sessionDuration');
  // delete TimeRange filter
  filters.value.filters = filters.value.filters.filter(it => it.filterProperties.type !== 'timeRange');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties.type !== 'timeRange');
  // delete Places filter
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties?.type !== 'taxonomyPlaces');
  // delete Themes filter
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties?.type !== 'taxonomyThemes');
  // delete Types filter
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties?.type !== 'taxonomyTypes');
  // delete Date Range filter
  filters.value.filters = filters.value.filters.filter(it => it.filterProperties.type !== 'date_range');
  // delete Search filter
  filters.value.sorts = filters.value.sorts.filter(it => it.type !== 'search');
  // delete crossedTaxonomyCount metrics
  if (filters.value.metrics) {
    filters.value.metrics = filters.value.metrics.map((metric) => {
      if (metric.type === 'crossedTaxonomyCount' && metric.crossedTaxonomies) {
        metric.crossedTaxonomies.themes = [];
        metric.crossedTaxonomies.places = [];
        metric.crossedTaxonomies.types = [];
      }
      return metric;
    });
  }

  // Force re-render of filter components to reset their internal state
  filtersResetKey.value++;
  if (isForceReset) {
    map.value.position(initialMapPosition.value);
    filtersStore.saveToStorage(filters.value);
    return;
  }

  filtersStore.saveToStorage(filters.value);
  await productsStore.fetchProductsList(filters.value);
}

async function showLastResults() {
  if (!lastValidBoundingBox.value || !map.value) {
    return;
  }

  const { center, range } = lastValidBoundingBox.value;
  const bounds = getBoundingBox(center, range);

  map.value.position({
    center,
    range,
    animate: true,
  });

  filtersStore.addBoundingBoxFilter(bounds);
  await productsStore.fetchProductsList(filters.value);
}

const distributorId = computed(() => taxonomyFilter.value?.filter.filter.distributor_id ?? '');
const taxonomyFilterName = computed(() => {
  const name = taxonomyFilter.value?.name.find(it => it.lang === 'fr-FR')?.value ?? null;
  if (!name)
    return 'Filtre Distributeur';
  return name[0].toUpperCase() + name.substring(1);
});
</script>

<template>
  <div class="main-container">
    <div v-if="!isMobile" class="slider-container">
      <div class="filters-and-products-wrapper">
        <div class="filter-container" :class="{ 'loading-disabled': isLoadingMapMovement }">
          <div class="filter-title">
            <h3>
              {{ t('map.filters') }}
            </h3>
            <button
              class="reset-button" :disabled="!hasActiveFilters(filters) || isLoadingMapMovement"
              @click="resetProductFilters(false)"
            >
              {{ t('search.cancel_button') }}
            </button>
          </div>
          <div :key="filtersResetKey" class="filters">
            <div class="search">
              <TheMapSearch />
            </div>
            <div v-if="hasCalendar">
              <TheCalendar :columns="1" :rows="1" />
              <hr>
            </div>
            <div v-if="themes.length">
              <h3>
                {{ t('filters.themes', { count: 2 }) }}
              </h3>
              <TheThemesFilter />
              <hr>
            </div>
            <div v-if="types.length">
              <h3>
                {{ t('filters.types', { count: 2 }) }}
              </h3>
              <TheTypeFilter />
              <hr>
            </div>
            <div>
              <h3>
                {{ t('menu.price') }}
              </h3>
              <ThePriceFilter :placeholder="t('menu.duration')" :min="0" :max="1500" :step="10" />
              <hr>
            </div>
            <div v-if="hasCalendar">
              <h3>
                {{ t('menu.duration') }}
              </h3>
              <TheDurationFilter :min="0" :max="24" :step="1" />
              <hr>
            </div>
            <div v-if="hasCalendar">
              <h3>
                {{ t('menu.moment') }}
              </h3>
              <TheTimeRangeFilter :placeholder="t('menu.moment')" :options="timeRangeOptions" />
              <hr>
            </div>
            <div v-if="places.length">
              <h3>
                {{ t('filters.places', { count: 2 }) }}
              </h3>
              <TheMultiselectFilter
                :placeholder="t('menu.place')" :options="places"
                @update:model-value="filtersStore.addPlacesFilters"
              />
              <hr>
            </div>
            <div v-if="taxonomyFilter?.taxonomies.length">
              <h3>
                {{ taxonomyFilterName }}
              </h3>
              <TheDistributorFilter
                :placeholder="taxonomyFilterName" :options="taxonomyFilter.taxonomies"
                @update:model-value="(data) => filtersStore.handleDistributorFilters(data, distributorId)"
              />
              <hr>
            </div>
          </div>
        </div>
        <div v-if="showProductSlider" class="products-container" :class="{ 'loading-disabled': isLoadingMapMovement }">
          <div class="sorting-title">
            <h4>
              {{ t('menu.sorting') }}
            </h4>
            <TheSelectDropdown
              :options="sortingOptions2" alignment="right"
              @update:model-value="(opt: any) => filtersStore.addSortingFilter(opt.value)"
            />
          </div>
          <TheMapList
            :is-loading-map-movement="isLoadingMapMovement" :has-last-valid-results="!!lastValidBoundingBox"
            @hover-product="handleHoverProduct" @show-last-results="showLastResults"
            @reset-all="resetProductFilters(true)"
          />
        </div>
        <div v-if="isLoadingMapMovement" class="loading-overlay">
          <div class="loading-container">
            <span>{{ t('map.loading') }}</span>
            <div class="loading-dots">
              <span />
              <span />
              <span />
            </div>
          </div>
          <button
            v-if="!!lastValidBoundingBox && !isLoadingMapMovement && products.length === 0"
            class="last-results-button-overlay" @click="showLastResults"
          >
            {{ t('map.show_closest_results') }}
          </button>
        </div>
      </div>
    </div>
    <div v-else>
      <button
        :aria-haspopup="true"
        aria-controls="filters-dialog"
        class="filter-button"
        :disabled="isLoadingMapMovement"
        @click="isFiltersOpen = !isFiltersOpen"
      >
        <SlidersHorizontal :size="20" color="var(--yb-color-black)" aria-hidden="true" />
        <span>{{ t('map.filter') }}</span>
      </button>
      <TheMobileSlider
        :is-open="isFiltersOpen" :title="t('menu.filters')" anchor="top" :footer="true"
        @close="isFiltersOpen = false" @validation="handleProductsFilter"
        @reset="resetProductFilters(false)"
      >
        <MobileProductFilters :key="filtersResetKey" :is-mode-map="true" />
      </TheMobileSlider>
    </div>
    <div v-if="currentProduct && isMobile" class="mobile-card">
      <TheCard
        :key="currentProduct.id"
        :responsive-row="false"
        :product="currentProduct"
        :show-favorite="true"
        :prefix-url="prefixUrl"
        :small="true"
        :show-close-button="true"
        @close-popup="currentProduct = undefined"
      />
    </div>
    <div v-if="isMobile && products.length === 0 && !isLoadingMapMovement" class="mobile-no-results">
      <div class="mobile-buttons">
        <button class="mobile-reset-all-button" @click="resetProductFilters(true)">
          {{ t('map.reset_all') }}
        </button>
        <button v-if="lastValidBoundingBox" class="mobile-last-results-button" @click="showLastResults">
          {{ t('map.show_closest_results') }}
        </button>
      </div>
    </div>
    <div id="map_wrapper" :class="{ expanded: !showProductSlider }">
      <div v-if="isLoadingMapMovement && isMobile" class="map-loading">
        <div class="loading-dots">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="css" scoped>
.main-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  z-index: 9999;
  display: flex;
  background-color: var(--yb-color-white);
}

hr {
  margin-block: var(--yb-spacing-base);
  border: 1px solid var(--yb-color-lightgray);
}

.slider-container {
  display: flex;
}

.filters-and-products-wrapper {
  display: flex;
  position: relative;
}

.filter-container {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 23vw;
  z-index: 10;
  background-color: var(--yb-color-white);

  .filter-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--yb-spacing-base);
    border-bottom: 1px solid var(--yb-color-mediumgray);
  }

  .filters {
    padding: 0 var(--yb-spacing-base);
    overflow-x: hidden;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    /* Hide scrollbar for Chrome, Safari and Opera */

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .reset-button {
    all: unset;
    background: none;
    border: none;
    color: var(--yb-color-primary);
    cursor: pointer;
    font-size: var(--yb-text-sm);
    font-weight: var(--yb-text-semi-bold);
    text-decoration: underline;

    &:hover:not(:disabled) {
      color: var(--yb-color-dark-primary);
    }

    &:disabled {
      color: var(--yb-color-mediumgray);
      cursor: not-allowed;
      text-decoration: none;
    }
  }

  .search {
    display: flex;
    padding: var(--yb-spacing-base) 0;
  }
}

.products-container {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 25vw;
  background-color: var(--yb-color-slidergray);
  box-shadow: 20px 0 20px -20px rgba(0, 0, 0, 0.05) inset;
  z-index: 5;

  .sorting-title {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--yb-spacing-sm);
    padding: var(--yb-spacing-base);
  }
}

#map_wrapper {
  width: 52%;
  height: 100%;
  display: flex;
  position: relative;

  &.expanded {
    width: 77%;
  }

  @media screen and (width < 1024px) {
    width: 100%;
  }
}

.filter-button {
  all: unset;
  display: flex;
  align-items: center;
  background-color: var(--yb-color-white);
  color: var(--yb-color-black);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-sm);
  height: 50px;
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 50;
  gap: var(--yb-spacing-xs);
  padding: 0 var(--yb-spacing-sm);
  font-weight: var(--yb-text-bold);

  &:disabled {
    opacity: 0.7;
    box-shadow: none;
  }
}

.mobile-card {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  width: 90%;
}

.mobile-no-results {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  width: 90%;
}

.mobile-buttons {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-sm);
  align-items: center;
}

.mobile-last-results-button,
.mobile-reset-all-button {
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-semi-bold);
  border-radius: var(--yb-radius-sm);
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;

  &:focus {
    outline: 2px solid var(--yb-color-primary);
    outline-offset: 2px;
  }
}

.mobile-last-results-button {
  background-color: var(--yb-color-primary);
  color: var(--yb-color-white);
  border: 2px solid var(--yb-color-primary);

  &:hover {
    background-color: var(--yb-color-primary-hover);
    border-color: var(--yb-color-primary-hover);
  }
}

.mobile-reset-all-button {
  background-color: var(--yb-color-white);
  color: var(--yb-color-primary);
  border: 2px solid var(--yb-color-primary);

  &:hover {
    background-color: var(--yb-color-primary);
    color: var(--yb-color-white);
  }
}

.map-loading {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  background-color: var(--yb-color-white);
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  border-radius: var(--yb-radius-sm);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--yb-color-lightgray);
}

.loading-dots {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
}

.loading-dots span {
  width: 6px;
  height: 6px;
  background: var(--yb-color-primary);
  border-radius: 50%;
  animation: pulse 1.4s ease-in-out infinite both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes pulse {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(248, 249, 250, 0.95);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  flex-direction: column;
  gap: var(--yb-spacing-xl);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--yb-spacing-base);
  padding: var(--yb-spacing-xl);
  background: var(--yb-color-white);
  border-radius: var(--yb-radius-base);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(var(--yb-theme-color), 0.1);
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-semi-bold);
  color: var(--yb-color-primary);
}

.last-results-button-overlay {
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  background-color: var(--yb-color-primary);
  color: var(--yb-color-white);
  border: none;
  border-radius: var(--yb-radius-sm);
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-semi-bold);
  cursor: pointer;
  transition: background-color 0.2s ease;
  z-index: 1001;

  &:hover {
    background-color: var(--yb-color-dark-primary);
  }

  &:focus {
    outline: 2px solid var(--yb-color-primary);
    outline-offset: 2px;
  }
}

.loading-disabled {
  pointer-events: none;
  opacity: 0.6;
  user-select: none;
}
</style>
